import dayjs from 'dayjs';

export type DateRangeValue = [string | null, string | null];
export type PeriodPoint = {
  year: number | null;
  month: number | null;
};
type DateRangeFieldStateLike = {
  fromYear: number | null;
  fromMonth: number | null;
  toYear: number | null;
  toMonth: number | null;
};

const DATE_FORMAT = 'YYYY-MM-DD';

export const MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

export const toIsoDate = (date: string | Date | null): string | null => {
  if (!date) {
    return null;
  }

  const parsedDate = dayjs(date);

  return parsedDate.isValid() ? parsedDate.format(DATE_FORMAT) : null;
};

export const formatDateRangeLabel = ([from, to]: DateRangeValue): string | null => {
  const fromLabel = from ? `${MONTHS_SHORT[dayjs(from).month()]} ${dayjs(from).year()}` : null;
  const toLabel = to ? `${MONTHS_SHORT[dayjs(to).month()]} ${dayjs(to).year()}` : null;

  if (fromLabel && toLabel) {
    return `${fromLabel} - ${toLabel}`;
  }

  if (fromLabel) {
    return `от ${fromLabel}`;
  }

  if (toLabel) {
    return `до ${toLabel}`;
  }

  return null;
};

export const getPeriodPointStart = ({ year, month }: PeriodPoint): string | null => {
  if (year === null) {
    return null;
  }

  return dayjs()
    .year(year)
    .month(month ?? 0)
    .date(1)
    .format(DATE_FORMAT);
};

export const getPeriodPointEnd = ({ year, month }: PeriodPoint): string | null => {
  if (year === null) {
    return null;
  }

  return dayjs()
    .year(year)
    .month(month ?? 11)
    .endOf('month')
    .format(DATE_FORMAT);
};

/** Mantine YearPickerInput: значение как YYYY-MM-DD (1 января выбранного года). */
export const getYearPickerIsoValue = (year: number | null): string | null => {
  if (year === null) {
    return null;
  }

  return getPeriodPointStart({ year, month: 0 });
};

/** Mantine MonthPickerInput: первый день выбранного месяца; null если месяц не выбран. */
export const getMonthPickerIsoValue = (year: number | null, month: number | null): string | null => {
  if (year === null || month === null) {
    return null;
  }

  return getPeriodPointStart({ year, month });
};

/** Граница minDate/maxDate для пикеров; undefined если год не задан. */
export const getPeriodPickerBoundaryDate = (year: number | null | undefined, month: number): string | undefined => {
  if (year === null || year === undefined) {
    return undefined;
  }

  return getPeriodPointStart({ year, month }) ?? undefined;
};

export const ensureValidDateRangeState = (state: DateRangeFieldStateLike): DateRangeFieldStateLike => {
  const { fromYear, fromMonth, toYear, toMonth } = state;

  if (fromYear === null || toYear === null) {
    return state;
  }

  if (fromYear > toYear) {
    return { ...state, toYear: fromYear, toMonth: null };
  }

  if (fromYear !== toYear || fromMonth === null || toMonth === null) {
    return state;
  }

  if (fromMonth > toMonth) {
    return { ...state, toMonth: fromMonth };
  }

  return state;
};
